<template>
  <div class="backdrop2" :class="{ active: showTaskDetailModal }" @click="close">
    <div class="modal2 task-modal" style="max-width: 64.6875rem; min-height: 40rem;" @click.stop>
      <i class="iconoir-xmark" style="z-index:10000" @click="close"></i>

      <simplebar style="max-height:550px;">
        <content-loading v-if="isLoading" />
        <div v-else>
          <div v-if="isForbidden">
            <h4 class="mt-5 pt-5 text-center">{{ $t("http.403") }}</h4>
            <div class="text-center font-size-24 mt-4 text-danger">
              <i class="fa fa-ban"></i>
            </div>
          </div>
          <div v-else-if="isEditing || isCreating">
            <task-edit-mode ref="editmode" @onFormError="onFormError" />
          </div>
          <div v-else>
            <task-read-mode @reloadwith="reloadwith" />
          </div>
        </div>
      </simplebar>
      <div class="border-top pt-3 modal-bottom">
        <sweet-button
          v-if="isCreating || isEditing"
          @click="save()"
          :loading="isUpdating"
          :disabled="isUpdating || hasError"
          variant="success"
          icon="mdi mdi-check"
          size="sm"
          buttonClass="ml-2"
        >
          {{ $t("common.save") }}
        </sweet-button>
        <sweet-button v-if="task" @click="edit()" variant="light" icon="bx bx-pencil" size="sm" buttonClass="ml-2">
          <template v-if="isEditing">
            {{ $t("common.cancel") }}
          </template>
          <template v-else>
            {{ $t("common.edit") }}
          </template>
        </sweet-button>

        <sweet-button v-if="task" @click="deleteById()" variant="danger" icon="bx bx-trash" size="sm" buttonClass="ml-2">
          {{ $t("common.delete") }}
        </sweet-button>
        <sweet-button v-if="task" @click="toggleArchive()" variant="secondary" icon="bx bx-archive" size="sm" buttonClass="ml-2">
          <span v-if="!task.archived">{{ $t("task.archive") }}</span>
          <span v-else>{{ $t("task.restore-archive") }}</span>
        </sweet-button>
      </div>
    </div>
  </div>
</template>

<script>
import simplebar from "simplebar-vue";
import TaskEditMode from "./task-edit-mode";
import { bulkResultMessage, hasAssignee, savePlan } from "@/modules/task/bulk-assign"; // TKP-121; TKP-250 g.3
import { EventBus } from "@modules/websocket";
import { definitionPayload, isPeriodicForm, periodicResultMessage } from "@/modules/task/periodic"; // TKP-122
import TaskReadMode from "./task-read-mode";
export default {
  components: { TaskReadMode, TaskEditMode, simplebar },
  data() {
    return {
      hasError: false,
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters["task/isLoading"];
    },
    isForbidden() {
      return this.$store.getters["task/isForbidden"];
    },
    isUpdating() {
      return this.$store.getters["task/isUpdating"];
    },
    isDisabled() {
      return this.isLoading;
    },
    isEditing() {
      return this.$store.getters["task/isEditing"];
    },
    isCreating() {
      return this.$store.getters["task/isCreating"];
    },
    isFormEnabled() {
      const form = this.$store.getters["task/form"];
      if (!form) {
        return false;
      }
      return Object.keys(form).length > 3;
    },
    showTaskDetailModal() {
      return this.$store.getters["task/showTaskDetailModal"];
    },
    task() {
      const o = this.$store.getters["task/task"];
      if (o) {
        return { ...o };
      }
      return undefined;
    },
  },
  methods: {
    edit() {
      this.reloadwith(this.task);
      this.$store.commit("task/isEditing", !this.isEditing);
    },
    async save({ skipAssignee = false } = {}) {
      const form = this.$store.getters["task/form"];
      // TKP-250 g.3: formdan kayıtta atanan kişi zorunlu (arşive alma/çıkarma eski görevi engellemez)
      if (!skipAssignee && !hasAssignee(form)) {
        this.$toast.warning(this.$t("task.assignee-required"));
        return;
      }

      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }

      try {
        if (this.currentProduct) {
          form.products = [this.currentProduct];
        }
        //tedbirlerde kullanılıyor
        if (!form._id) {
          form.taskDefinitionId = "id-user-" + new Date().getTime();
        }

        // TKP-121: birden çok atanan → atanan başına ayrı görev (`/task/bulk`); tek atanan → bugünkü yol birebir
        const plan = savePlan(form);
        let data;
        if (isPeriodicForm(form)) {
          // TKP-122: periyot seçildi → periyodik tanım; ilk dönemin görevi sunucuda hemen üretilir
          const response = await this.$store.dispatch("periodicTask/create", definitionPayload(form));
          const message = periodicResultMessage(response.data, (k, p) => this.$t(k, p));
          this.$toast[message.variant === "success" ? "success" : "warning"](message.text);
          data = response.data && response.data.definition;
        } else if (plan.mode === "bulk") {
          const response = await this.$store.dispatch("task/createMany", { item: plan.item, assignees: plan.assignees });
          const result = response.data;
          const message = bulkResultMessage(result, (k, p) => this.$t(k, p));
          this.$toast[message.variant === "success" ? "success" : message.variant === "warning" ? "warning" : "error"](message.text);
          data = (result.created || [])[0];
        } else {
          ({ data } = await this.$store.dispatch("task/save", plan.payload));
          this.$toast.success(this.$t("common.success"));
        }
        this.$emit("onSave", data);
        EventBus.$emit("task-board-update", { local: true }); // TKP-250 g.2: sunucu yalnız durum/atanan/arşiv değişince yayınlıyor — liste her kayıtta tazelenir
        this.$store.commit("task/form", undefined);
        this.$store.commit("task/task", undefined);
        if (this.$refs.editmode) {
          this.$refs.editmode.form = {};
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async toggleArchive() {
      this.$store.commit("task/form", { ...this.task, archived: !this.task.archived });
      this.save({ skipAssignee: true });
    },
    async deleteById() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`task/delete`, this.task._id);
        this.$emit("onDelete", this.task);
        EventBus.$emit("task-board-update", { local: true }); // TKP-250 g.2
        this.$store.commit("task/form", undefined);
        this.$store.commit("task/task", undefined);
        this.$toast.success(this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    reloadwith(task) {
      return this.$store.dispatch("task/fetchDetail", task._id);
    },

    close() {
      this.$store.commit("task/showTaskDetailModal", false);
    },
    onFormError(err) {
      this.hasError = !!err;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../modules/task/tasks.scss";
</style>

<style scoped>
.modal2 {
  position: relative;
}
.modal-bottom {
  position: absolute;
  bottom: 0;
  margin-bottom: 15px;
  padding-top: 15px;
}
</style>
